import { STUDY_ANSWER_TEXT, STUDY_PRACTICE_SHELF_ID } from '../../constants/eyetrackingConstants';

interface ShelfProduct {
  name?: string;
  image_filename?: string;
  quantity?: number | null;
}

interface ShelfEntry {
  shelf_ref?: string;
  shop_time_ms?: number | null;
  products?: ShelfProduct[];
}

/** What the backend stores as the answer to an embedded study screen. */
export interface StudyAnswer {
  quality_grade?: string | null;
  shopper_metrics: ShelfEntry[];
}

export interface ShelfSummary {
  shelf: string;
  seconds: number | null;
  bought: string | null;
  putBack: string[];
}

export const isStudyAnswer = (value: unknown): value is StudyAnswer =>
  typeof value === 'object' &&
  value !== null &&
  Array.isArray((value as { shopper_metrics?: unknown }).shopper_metrics);

/** "P17_tillamook_country_smoker.png" → "Tillamook Country Smoker". */
export const packLabel = (fileName: string): string =>
  fileName
    .replace(/\.[a-z0-9]+$/i, '')
    .replace(/^P\d+_/, '')
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

const fileOf = (p: ShelfProduct): string => p.image_filename || p.name || '';

/** One line per shelf in shopped order: the pack bought (quantity > 0) and the packs put back. */
export function summarizeStudyAnswer(answer: StudyAnswer): ShelfSummary[] {
  return answer.shopper_metrics.map((entry) => {
    const products = entry.products ?? [];
    const boughtFile = products.find((p) => (p.quantity ?? 0) > 0);
    const bought = boughtFile ? fileOf(boughtFile) : null;
    const putBack = [...new Set(products.map(fileOf))].filter((f) => f && f !== bought);
    const shelf =
      entry.shelf_ref === STUDY_PRACTICE_SHELF_ID
        ? STUDY_ANSWER_TEXT.practice
        : (entry.shelf_ref ?? '');
    const seconds =
      typeof entry.shop_time_ms === 'number' ? Math.round(entry.shop_time_ms / 100) / 10 : null;
    return { shelf, seconds, bought: bought && packLabel(bought), putBack: putBack.map(packLabel) };
  });
}

/** Plain-text form for the text/conversation view. */
export function studyAnswerText(answer: StudyAnswer): string {
  const shelves = summarizeStudyAnswer(answer).map((s) => {
    const choice = s.bought ?? STUDY_ANSWER_TEXT.nothing;
    const extra = s.putBack.length
      ? ` (${STUDY_ANSWER_TEXT.putBack}: ${s.putBack.join(', ')})`
      : '';
    return `${s.shelf}: ${choice}${extra}`;
  });
  const quality = `${STUDY_ANSWER_TEXT.quality}: ${answer.quality_grade ?? STUDY_ANSWER_TEXT.notAvailable}`;
  return [quality, ...shelves].join(' · ');
}
